import { useState } from "react";
import { BarChart3, Download, Star, Users, Lock } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { PageHeader } from "@/components/control/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Info, ActionButton, TextField, PageView, PageButtons, BlockedAside, useLoad, can, fmt, saveBlob, blobError,
} from "@/components/control/kit";
import { categoryLabel } from "@/components/control/activityFields";

const num = (v) => (typeof v === "number" ? v : 0);

function Chart({ data, x, testId }) {
  if (!data.length) return <Empty text="Keine Daten im Zeitraum." />;
  return (
    <div className="h-64" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.map((d) => ({ ...d, n: num(d.value) }))} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey={x} stroke="#6b7280" fontSize={12} />
          <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} />
          <Tooltip formatter={(_, __, p) => [p.payload.value, "Anzahl"]} />
          <Bar dataKey="n" fill="#2A1650" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ExportButton({ from, to }) {
  return (
    <ActionButton label="Aggregiert exportieren" icon={Download} testId="pilot-export" submitLabel="Export prüfen und erstellen"
      page="export" title="Aggregierte Auswertung exportieren" pageSubtitle="Der Export enthält nur zusammengefasste Kennzahlen."
      description="Nur zusammengefasste Werte. Werte unter 5 werden als „<5“ ausgegeben. Der Export wird protokolliert."
      fields={[
        { name: "metrics", label: "Kennzahlen", type: "checks", required: true, options: [
          { value: "signups", label: "Registrierungen je Monat" }, { value: "participation", label: "Teilnahmen nach Kategorie" },
          { value: "participation_by_day", label: "Teilnahmen nach Tag und Land" },
          { value: "participation_by_week", label: "Teilnahmen nach Kalenderwoche und Land" }] },
        { name: "purpose", label: "Zweck", type: "textarea", required: true },
      ]}
      onSubmit={async (v) => {
        try {
          const res = await api.post("/pilot/export", { ...v, date_from: from, date_to: to }, { responseType: "blob" });
          saveBlob(res.data, "pilot-auswertung.csv");
          return "Export erstellt.";
        } catch (err) {
          throw await blobError(err);
        }
      }} />
  );
}

function WeekTable({ rows }) {
  if (!rows.length) return <Empty text="Keine bestätigten Teilnahmen im Zeitraum." testId="pilot-week-empty" />;
  const countries = [...new Set(rows.map((r) => r.country))].sort();
  const weeks = [...new Set(rows.map((r) => r.week))].sort().reverse();
  const cell = Object.fromEntries(rows.map((r) => [`${r.week}|${r.country}`, r.value]));
  return (
    <div className="overflow-x-auto" data-testid="pilot-week-table">
      <table className="w-full min-w-[320px] text-left text-sm">
        <caption className="sr-only">Bestätigte Teilnahmen nach Kalenderwoche und Land</caption>
        <thead><tr className="border-b border-border text-xs text-muted-foreground"><th scope="col" className="py-2 pr-3">Woche</th>
          {countries.map((c) => <th key={c} scope="col" className="py-2 pr-3">{c === "unbekannt" ? "Land unbekannt" : c}</th>)}</tr></thead>
        <tbody>
          {weeks.map((w) => (
            <tr key={w} className="border-b border-border/60"><th scope="row" className="py-2 pr-3 font-medium text-brand-purple">{w}</th>
              {countries.map((c) => <td key={c} className="py-2 pr-3">{cell[`${w}|${c}`] ?? 0}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Tiles({ items, testId }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid={testId}>
      {items.map(([k, v]) => (
        <div key={k} className="min-w-0 rounded-xl border border-[#E6E1F0] bg-white p-4" data-testid={`${testId}-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <p className={`break-words font-bold text-brand-purple ${typeof v === "number" ? "text-2xl" : "text-base leading-snug"}`}>{v}</p>
          <p className="mt-1 text-xs text-zinc-600">{k}</p>
        </div>
      ))}
    </div>
  );
}

function Bars({ title, rows, testId }) {
  const max = Math.max(1, ...rows.map((r) => num(r.value)));
  return (
    <section className="rounded-2xl border border-[#E6E1F0] bg-white p-5" data-testid={testId}>
      <h2 className="font-bold text-brand-purple">{title}</h2>
      {rows.length === 0 ? <Empty text="Zu wenige Daten für eine Verteilung." /> : (
        <ul className="mt-4 space-y-3">
          {rows.map((r) => (
            <li key={r.label} className="grid grid-cols-[6rem_1fr_2.5rem] items-center gap-3 text-sm" data-testid={`${testId}-row`}>
              <span className="font-semibold text-brand-purple">{r.label}</span>
              <span className="h-2 overflow-hidden rounded-full bg-[#EEEAF7]" aria-hidden="true">
                <span className="block h-full rounded-full bg-brand-magenta" style={{ width: `${(num(r.value) / max) * 100}%` }} />
              </span>
              <span className="text-right font-bold text-brand-purple">{r.value}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-xs text-zinc-600">Werte unter 5 erscheinen als „&lt;5“.</p>
    </section>
  );
}

const PILOT_ASIDE = (
  <>
    <BlockedAside icon={BarChart3} lavender title="Aussagekraft" text="Zusagen und tatsächliche Teilnahme sind getrennte Ereignisse. Freiwillige Rückmeldungen sind keine Wirkungsmessung." />
    <BlockedAside icon={Lock} title="Privatsphäre" text="Keine Bewertung von Einzelpersonen, Diagnosen oder Bewegungsprofilen." />
  </>
);

function FeedbackPage({ d }) {
  const f = d.feedback;
  return (
    <PageView page="rueckmeldungen" label="Rückmeldungen auswerten" icon={Star} testId="pilot-feedback-view" title="Freiwillige Rückmeldungen auswerten"
      subtitle="Alle Kennzahlen werden aus derselben Rückmeldungsmenge berechnet." aside={PILOT_ASIDE}>
      {({ back }) => (
        <>
          <Tiles testId="pilot-feedback-tiles" items={[["Freiwillige Rückmeldungen", f.count], ["Mittelwert", typeof f.average === "number" ? f.average.toFixed(1).replace(".", ",") : "erst ab fünf Rückmeldungen"],
            ["Punkteskala", "1 bis 5"], ["Stand", fmt(d.generated_at)]]} />
          <Bars title="Verteilung aus derselben Datenbasis" testId="pilot-feedback-distribution"
            rows={(f.distribution || []).map((r) => ({ label: `${r.rating} ${r.rating === 1 ? "Punkt" : "Punkte"}`, value: r.value }))} />
          <PageButtons back={back} testId="pilot-feedback-view" />
        </>
      )}
    </PageView>
  );
}

function ParticipationPage({ d, from, to }) {
  const period = from || to ? `${from || "Beginn"} bis ${to || "heute"}` : "Gesamter Zeitraum";
  return (
    <PageView page="teilnahme" label="Teilnahme im Pilot" icon={Users} testId="pilot-participation-view" title="Teilnahme im Pilot"
      subtitle="Zusage und tatsächliche Teilnahme sind getrennte Ereignisse." aside={PILOT_ASIDE}>
      {({ back }) => (
        <>
          <Tiles testId="pilot-participation-tiles" items={[["Zeitraum", period], ["Bestätigte Teilnahmen", d.participation_total ?? 0],
            ["Wahrgenommene Teilnahmen", "nicht erfasst"], ["Freiwillige Rückmeldungen", d.feedback.count]]} />
          <Bars title="Bestätigte Teilnahmen nach Kategorie" testId="pilot-participation-bars"
            rows={d.participation.map((p) => ({ label: categoryLabel(p.category_key), value: p.value }))} />
          <p className="rounded-xl bg-[#EEEAF7] px-4 py-3 text-sm text-brand-purple" data-testid="pilot-attendance-note">Eine tatsächliche Anwesenheit wird in der Mitglieder-App nicht erfasst und deshalb nicht ausgewiesen.</p>
          <PageButtons back={back} testId="pilot-participation-view" />
        </>
      )}
    </PageView>
  );
}

function PilotView({ d, from, to }) {
  const s = d.summary;
  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2" data-testid="pilot-detail-links">
        <FeedbackPage d={d} />
        <ParticipationPage d={d} from={from} to={to} />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="pilot-kpis">
        {[["Mitglieder", s.total_members], ["Verifiziert", s.verified_members], ["Aktivitäten live", s.activities_live],
          ["Prüfungen abgeschlossen", s.reviews_completed], ["Zufriedenheit (1–5)", s.satisfaction ?? "zu wenige Rückmeldungen"]].map(([k, v]) => (
          <div key={k} className="min-w-0 rounded-xl border border-border bg-white p-4"><p className={`break-words font-bold text-brand-purple ${typeof v === "number" ? "text-2xl" : "text-base leading-snug"}`}>{v}</p><p className="text-xs text-muted-foreground">{k}</p></div>
        ))}
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Section title="Registrierungen je Monat" testId="pilot-signups"><Chart data={d.signups} x="month" testId="pilot-signups-chart" /></Section>
        <Section title="Teilnahme im Pilot nach Kategorie" testId="pilot-participation">
          <Chart data={d.participation.map((p) => ({ ...p, category: categoryLabel(p.category_key) }))} x="category" testId="pilot-participation-chart" />
        </Section>
      </div>
      <Section title="Teilnahme im Pilot nach Woche und Land" className="mt-5" testId="pilot-participation-week">
        <WeekTable rows={d.participation_by_week || []} />
        <p className="mt-3 text-xs text-muted-foreground">{d.definitions.participation_by_week} Werte unter 5 erscheinen als „&lt;5“.</p>
      </Section>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Section title="Freiwillige Rückmeldungen" testId="pilot-feedback">
          <Info items={[["Anzahl Rückmeldungen", d.feedback.count], ["Durchschnitt", d.feedback.average ?? "erst ab fünf Rückmeldungen"]]} />
          <p className="mt-3 text-xs text-muted-foreground">Rückmeldungen werden in der Mitglieder-App freiwillig abgegeben und nur zusammengefasst angezeigt.</p>
        </Section>
        <Section title="Definitionen" testId="pilot-definitions">
          <dl className="space-y-2 text-sm">{Object.entries(d.definitions).map(([k, v]) => <div key={k}><dd>{v}</dd></div>)}</dl>
          <p className="mt-3 text-xs text-muted-foreground">Stand: {fmt(d.generated_at)}</p>
        </Section>
      </div>
    </>
  );
}

export default function Pilot() {
  const { user } = useAuth();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const st = useLoad(`/pilot?${new URLSearchParams({ date_from: from, date_to: to })}`);
  return (
    <div>
      <PageHeader title="Pilot & Auswertung" subtitle="Echte, zusammengefasste Kennzahlen aus der Mitglieder-App." icon={BarChart3}
        actions={can(user, "pilot:export") && <ExportButton from={from} to={to} />} />
      <div className="mb-5 grid max-w-lg grid-cols-2 gap-3">
        <TextField id="pilot-from" label="Von" type="date" value={from} onChange={setFrom} testId="pilot-from" />
        <TextField id="pilot-to" label="Bis" type="date" value={to} onChange={setTo} testId="pilot-to" />
      </div>
      <Loadable state={st}>{(d) => <PilotView d={d} from={from} to={to} />}</Loadable>
    </div>
  );
}
